import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import has from 'lodash/has';
import classnames from 'classnames';
import getStatValue from '~/util/getStatValue';
import StyledComponent from '~/components/Styled/Styled';
import Follow from '~/components/Follow/Follow';
import TeamMenu from '~/components/TeamMenu/TeamMenu';
import { ResponsivePNG } from 'sports-react';
import css from './TeamBanner.scss';

const cssContext = classnames.bind(css);
class TeamBanner extends React.Component {
  constructor(props) {
    super(props);
    this.handleScroll = this.handleScroll.bind(this);
    this.state = {
      stickyMenu: false,
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.handleScroll);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll(_) {
    const scrollTop = document.scrollingElement.scrollTop;
    // 185px is where the menu reaches the top
    // 108px is where the top of the banner is in a normal state. This will prevent the sticky menu from appearing in the interstitial.
    const stickyMenu =
      scrollTop >= 185 && this.banner && this.banner.getBoundingClientRect().top < 100;
    this.setState({ stickyMenu });
  }

  render() {
    const { team, currentSection } = this.props;
    const teamLogoSmall = get(team, 'logos.small', '');
    const teamLogoLarge = get(team, 'logos.large', '');
    const menuClasses = cssContext({
      [css.stickyMenu]: this.state.stickyMenu,
    });
    const formattedRank = get(team, 'standing.formatted_rank', null);
    let recentRecord;
    let recentRecordLabel = 'L10';

    if (team.standing && team.standing.last_ten_games_record) {
      recentRecord = getStatValue(team, 'standing.last_ten_games_record');
    } else if (team.standing && team.standing.last_five_games_record) {
      recentRecord = getStatValue(team, 'standing.last_five_games_record');
      recentRecordLabel = 'L5';
    } else {
      recentRecord = '-';
    }

    // Set the Recent Record Label depending on the last games record availability.
    if (has(team.standing, 'last_five_games_record')) {
      recentRecordLabel = 'L5';
    } else if (has(team.standing, 'last_ten_games_record')) {
      recentRecordLabel = 'L10';
    }

    return (
      <div
        ref={node => {
          this.banner = node;
        }}
      >
        <div className={css.mobileView}>
          <div className={css.followButton}>
            <Follow />
          </div>
          <div className={css.stats}>
            <div className={css.statCell}>
              <div className={css.stat}>{recentRecord}</div>
              <div className={css.statName}>{recentRecordLabel}</div>
            </div>
            <div className={css.logo}>
              {teamLogoSmall ? (
                <ResponsivePNG src={teamLogoSmall} />
              ) : (
                <div className={css.logoDefault} />
              )}
            </div>
            <div className={css.statCell}>
              <div className={css.stat}>{getStatValue(team, 'standing.streak')}</div>
              <div className={css.statName}>Streak</div>
            </div>
          </div>
          <div className={css.info}>
            <div className={css.teamName}>{getStatValue(team, 'full_name')}</div>
            <div className={css.teamStats}>{formattedRank}</div>
          </div>
        </div>

        <div className={css.desktopView}>
          <div className={css.logo}>
            {teamLogoLarge ? (
              <ResponsivePNG src={teamLogoLarge} />
            ) : (
              <div className={css.logoDefault} />
            )}
          </div>
          <div className={css.info}>
            <div className={css.teamName}>{getStatValue(team, 'full_name')}</div>
            <div className={css.teamStats}>{formattedRank}</div>
          </div>
          <div className={css.stats}>
            <div className={css.statCell}>
              <div className={css.stat}>{getStatValue(team, 'standing.short_record')}</div>
              <div className={css.statName}>Record</div>
            </div>
            <div className={css.statCell}>
              <div className={css.stat}>{recentRecord}</div>
              <div className={css.statName}>{recentRecordLabel}</div>
            </div>
            <div className={css.statCell}>
              <div className={css.stat}>{getStatValue(team, 'standing.streak')}</div>
              <div className={css.statName}>Streak</div>
            </div>
          </div>
        </div>
        <div className={menuClasses}>
          <div className={css.teamMenu}>
            <TeamMenu team={team} currentSection={currentSection} />
          </div>
          {this.state.stickyMenu && (
            <div className={css.stickyFollow}>
              <Follow showText={false} />
            </div>
          )}
        </div>
        {this.state.stickyMenu && <div className={`${css.mobileView} ${css.blocker}`} />}
      </div>
    );
  }
}

TeamBanner.displayName = 'TeamBanner';

TeamBanner.propTypes = {
  team: PropTypes.shape({
    full_name: PropTypes.string.isRequired,
    logos: PropTypes.shape({
      large: PropTypes.string.isRequired,
      small: PropTypes.string.isRequired,
    }).isRequired,
    standing: PropTypes.shape({
      streak: PropTypes.string.isRequired,
      last_ten_games_record: PropTypes.string,
      last_five_games_record: PropTypes.string,
      short_record: PropTypes.string.isRequired,
      formatted_rank: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  currentSection: PropTypes.string,
};

TeamBanner.defaultProps = {
  currentSection: 'news',
};

export default StyledComponent(TeamBanner, [css]);
